import { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, ExternalLink, Edit, Trash2, Sparkles, BookOpen } from "lucide-react";

const emptyForm = { title: "", description: "", url: "", category: "General", active: true };
const CATEGORIES = ["Banking", "Investing", "Lending", "Taxes", "Benefits", "General"];

export default function Resources() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const load = async () => { const { data } = await api.get("/resources"); setRows(data); };
  // Load once on mount — save/remove call `load` directly.
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (k) => (e) => setForm({ ...form, [k]: k === "active" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/resources/${editingId}`, form);
      else await api.post("/resources", form);
      toast.success("Resource saved");
      setOpen(false); setForm(emptyForm); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this resource?")) return;
    await api.delete(`/resources/${id}`); toast.success("Removed"); load();
  };
  const openEdit = (r) => { setForm({ ...emptyForm, ...r }); setEditingId(r.id); setOpen(true); };

  // Group by category
  const grouped = rows.reduce((acc, r) => {
    (acc[r.category || "General"] = acc[r.category || "General"] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4" /> § RESOURCES</div>
          <h1 className="font-heading font-black text-5xl tracking-tight">Employee Perks</h1>
          <p className="text-sm text-muted-foreground mt-2">{isAdmin ? "Curate affiliate offers and benefits shown to your employees in their portal." : "Handpicked partner offers and benefits from your employer."}</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <a
              data-testid="download-handbook-btn"
              href={`${API}/handbook/pdf`}
              target="_blank"
              rel="noreferrer"
              className="border-2 border-ink px-5 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-ink hover:text-white transition-colors"
              title="The full operating manual, generated from your live configuration"
            >
              <BookOpen className="w-4 h-4" /> Director's Handbook
            </a>
            <button data-testid="add-resource-btn" onClick={() => { setEditingId(null); setForm(emptyForm); setOpen(true); }} className="hard-btn px-5 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Resource
            </button>
          </div>
        )}
      </div>

      {Object.keys(grouped).length === 0 && <div className="hard-card p-10 text-center text-muted-foreground font-mono">No resources yet.</div>}
      {Object.entries(grouped).sort().map(([cat, list]) => (
        <div key={cat} className="mb-8">
          <div className="overline mb-3">{cat.toUpperCase()}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((r) => (
              <div key={r.id} className="hard-card p-5 flex flex-col" data-testid={`resource-card-${r.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="font-heading font-black text-lg leading-tight">{r.title}</div>
                  {!r.active && isAdmin && <span className="px-1.5 py-0.5 bg-muted text-ink text-[10px] uppercase tracking-widest">Hidden</span>}
                </div>
                <p className="font-mono text-xs text-muted-foreground mb-4 flex-1 leading-relaxed">{r.description}</p>
                <div className="flex gap-2">
                  <a data-testid={`open-resource-${r.id}`} href={r.url} target="_blank" rel="noreferrer noopener" className="hard-btn flex-1 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2">
                    <ExternalLink className="w-3.5 h-3.5" /> Open
                  </a>
                  {isAdmin && (
                    <>
                      <button data-testid={`edit-resource-${r.id}`} onClick={() => openEdit(r)} className="border-2 border-ink p-2 hover:bg-ink hover:text-white"><Edit className="w-3.5 h-3.5" /></button>
                      <button data-testid={`delete-resource-${r.id}`} onClick={() => remove(r.id)} className="border-2 border-bad text-bad p-2 hover:bg-bad hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {open && isAdmin && (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4">
          <form onSubmit={save} className="bg-white border-2 border-ink w-full max-w-lg shadow-hard-lg">
            <div className="border-b-2 border-ink px-5 py-4"><div className="overline">§ RESOURCE</div><h2 className="font-heading font-black text-xl">{editingId ? "Edit Resource" : "Add Resource"}</h2></div>
            <div className="p-5 space-y-4">
              <label className="block"><div className="overline mb-2">Title</div><input required data-testid="res-title" value={form.title} onChange={upd("title")} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" /></label>
              <label className="block"><div className="overline mb-2">Description</div><textarea data-testid="res-desc" value={form.description} onChange={upd("description")} rows={2} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" /></label>
              <label className="block"><div className="overline mb-2">URL (with your affiliate params)</div><input required data-testid="res-url" type="url" value={form.url} onChange={upd("url")} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" /></label>
              <label className="block"><div className="overline mb-2">Category</div>
                <select data-testid="res-category" value={form.category} onChange={upd("category")} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2 font-mono text-sm"><input type="checkbox" data-testid="res-active" checked={form.active} onChange={upd("active")} /> Active (visible to employees)</label>
            </div>
            <div className="border-t-2 border-ink px-5 py-4 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border-2 border-ink font-mono text-xs uppercase tracking-widest">Cancel</button>
              <button data-testid="save-resource" type="submit" className="hard-btn px-4 py-2 font-mono text-xs uppercase tracking-widest">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
